import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle } from "lucide-react";

// NHS / primary-care email domains that pass without a warning.
// Mirrors the whitelist used by the super admin moderation panel so that
// what we tell the PM here matches what the moderator later sees.
const NHS_DOMAIN_SUFFIXES = [
  "@nhs.scot", ".nhs.scot",
  "@nhs.uk", ".nhs.uk",
  "@nhs.net", ".nhs.net",
  "@gov.scot", ".gov.scot",
];

function looksLikeNhsEmail(email) {
  const lower = (email || "").trim().toLowerCase();
  return NHS_DOMAIN_SUFFIXES.some((s) => lower.endsWith(s));
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("invite"); // "invite" | "create"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [roleInPractice, setRoleInPractice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Live advisory for PMs creating a practice — real-time, not blocking.
  const emailIsNhs = useMemo(() => looksLikeNhsEmail(email), [email]);
  const showNonNhsAdvisory = mode === "create" && email && !emailIsNhs;
  const practiceNameLooksReal = (practiceName || "").trim().length >= 4;

  const ROLE_OPTIONS = [
    { value: "", label: "— Select (optional) —" },
    { value: "receptionist", label: "Receptionist" },
    { value: "secretary", label: "Secretary" },
    { value: "workflow_admin", label: "Workflow admin" },
    { value: "prescription_clerk", label: "Prescription clerk" },
    { value: "office_manager", label: "Assistant / Office Manager" },
    { value: "clinician", label: "Clinical (nurse, HCA, ANP, pharmacist)" },
    { value: "pm", label: "Practice Manager" },
    { value: "new_starter", label: "New starter" },
    { value: "other", label: "Other / Attached" },
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const extras = mode === "invite"
      ? { invite_code: inviteCode.trim().toUpperCase() }
      : { practice_name: practiceName.trim() };
    if (roleInPractice) extras.role_in_practice = roleInPractice;
    if (mode === "invite" && !extras.invite_code) {
      setError("Enter the invite code your Practice Manager gave you.");
      return;
    }
    if (mode === "create" && !extras.practice_name) {
      setError("Enter the name of the practice you're creating.");
      return;
    }
    setBusy(true);
    const res = await register(email, password, name, extras);
    setBusy(false);
    if (res.ok) {
      if (mode === "create") {
        toast.success("Practice created — you're the Practice Manager");
      } else {
        toast.success("Account created — welcome to Calside");
      }
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <Layout>
      <div className="grid lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-4">Create account</div>
          <h1 className="font-serif text-5xl text-brand-ink leading-tight">Set up your own learning ledger.</h1>
          <p className="mt-4 text-brand-body">Progress, quiz scores and your certificate all live under your name — inside your practice.</p>

          <div className="mt-8 space-y-4 text-sm text-brand-body">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Joining an existing practice?</div>
              <p>Ask your Practice Manager for the invite code — it lets you join their team so your progress shows on their dashboard.</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Setting up a new practice?</div>
              <p>Create one and you become the Practice Manager. You&apos;ll get an invite code to share with your team.</p>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-white border border-brand-linen rounded-3xl p-8 shadow-card space-y-4">
          <div className="grid grid-cols-2 gap-2 p-1 bg-brand-bone border border-brand-linen rounded-full">
            <button
              type="button"
              data-testid={T.registerModeInvite}
              onClick={() => setMode("invite")}
              className={`py-2 rounded-full text-sm transition-colors ${mode === "invite" ? "bg-brand-ink text-brand-bone" : "text-brand-ink hover:bg-white"}`}
            >
              I have an invite code
            </button>
            <button
              type="button"
              data-testid={T.registerModeCreate}
              onClick={() => setMode("create")}
              className={`py-2 rounded-full text-sm transition-colors ${mode === "create" ? "bg-brand-ink text-brand-bone" : "text-brand-ink hover:bg-white"}`}
            >
              Create a practice
            </button>
          </div>

          {mode === "create" && (
            <div
              data-testid="register-pm-advisory"
              className="rounded-2xl border border-brand-secondary/30 bg-brand-secondary/5 p-4"
            >
              <div className="flex items-start gap-2.5">
                <span className="flex-shrink-0 rounded-full p-1.5 bg-brand-secondary/15 text-brand-secondary mt-0.5">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.8} />
                </span>
                <div className="text-sm text-brand-body">
                  <div className="font-medium text-brand-ink mb-1">Setting up your practice? Please use:</div>
                  <ul className="list-disc list-outside ml-5 space-y-1">
                    <li>Your <span className="font-medium text-brand-ink">NHS email</span> (<span className="font-mono text-xs text-brand-ink">@nhs.scot</span>, <span className="font-mono text-xs text-brand-ink">@nhs.uk</span>, <span className="font-mono text-xs text-brand-ink">@nhs.net</span> or <span className="font-mono text-xs text-brand-ink">@gov.scot</span>) — it tells us you're a real primary-care colleague and avoids a verification call.</li>
                    <li>Your <span className="font-medium text-brand-ink">actual practice name</span> (as it appears on your signage or your NHS Scotland listing) — not an abbreviation or a nickname. Your team will see it on their certificates and dashboards.</li>
                  </ul>
                  <div className="text-xs text-brand-muted mt-2 italic">
                    If you don't have an NHS email, you can still register — but we may contact you to verify.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Your name</label>
            <input data-testid={T.nameInput} required autoFocus value={name} onChange={(e) => setName(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Email</label>
            <input data-testid={T.emailInput} type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
            {showNonNhsAdvisory && (
              <div data-testid="register-email-non-nhs-warning" className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                <span>Looks like this isn't an NHS email. You can still register, but a Scottish NHS address (<span className="font-mono">@nhs.scot</span>, <span className="font-mono">@nhs.uk</span>, <span className="font-mono">@nhs.net</span>, <span className="font-mono">@gov.scot</span>) gets you through automatic verification.</span>
              </div>
            )}
            {mode === "create" && emailIsNhs && (
              <div data-testid="register-email-nhs-ok" className="mt-2 flex items-center gap-2 text-xs text-brand-secondary">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.8} /> NHS email — verified automatically.
              </div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Password</label>
            <input data-testid={T.passwordInput} type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
            <div className="text-xs text-brand-muted mt-1">Minimum 6 characters.</div>
          </div>

          {mode === "invite" ? (
            <div>
              <label className="text-xs uppercase tracking-widest text-brand-muted">Practice invite code</label>
              <input
                data-testid={T.inviteCodeInput}
                required
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="e.g. BRAIDCRAF-4JFY"
                className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors font-mono tracking-wider"
              />
              <div className="text-xs text-brand-muted mt-1">Case-insensitive. Ask your PM if you don&apos;t have one.</div>
            </div>
          ) : (
            <div>
              <label className="text-xs uppercase tracking-widest text-brand-muted">Practice name</label>
              <input
                data-testid={T.practiceNameInput}
                required
                value={practiceName}
                onChange={(e) => setPracticeName(e.target.value)}
                placeholder="e.g. Riverside Medical Practice"
                className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors"
              />
              <div className="text-xs text-brand-muted mt-1">
                Use the full, official practice name as it appears on NHS Scotland listings or your signage. Your team will see this on every certificate — abbreviations and nicknames look unprofessional and are a common moderation flag.
              </div>
              {practiceName && !practiceNameLooksReal && (
                <div data-testid="register-practice-name-short" className="mt-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                  <span>That looks short — please use the full practice name (minimum 4 characters).</span>
                </div>
              )}
            </div>
          )}

          {error && <div data-testid={T.authError} className="text-sm text-brand-accent bg-brand-accent/10 border border-brand-accent/20 rounded-xl px-4 py-3">{error}</div>}

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Your role at the practice</label>
            <select
              data-testid="role-in-practice"
              value={roleInPractice}
              onChange={(e) => setRoleInPractice(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="text-xs text-brand-muted mt-1">Used to suggest a starter path of courses for your role. You can change it later in your profile.</div>
          </div>

          <button data-testid={T.registerSubmit} disabled={busy} type="submit"
            className="w-full bg-brand-ink text-brand-bone rounded-full py-3 font-medium hover:bg-brand-inkHover disabled:opacity-60 transition-colors">
            {busy ? "Creating…" : (mode === "create" ? "Create practice & account" : "Create my account")}
          </button>
          <div className="text-sm text-brand-muted text-center">
            Already registered? <Link to="/login" className="text-brand-ink underline underline-offset-4">Sign in</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}
